import React, { createContext, useContext, useState, useEffect } from "react";
import Login from "../components/Login";
import {
  retryPendingRequest,
  cancelPendingRequest,
} from "../services/apiClient";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children, applicationName }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [showReAuthModal, setShowReAuthModal] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const storedUserData = sessionStorage.getItem("userData");

    if (token && storedUserData) {
      setIsAuthenticated(true);
      setUserData(JSON.parse(storedUserData));
    }

    setIsLoading(false);
  }, []);

  // Login function
  const login = async (data) => {
    sessionStorage.setItem("token", data.token);
    sessionStorage.setItem("userData", JSON.stringify(data));
    setIsAuthenticated(true);
    setUserData(data);

    // If this was a re-authentication, retry the pending request
    if (showReAuthModal) {
      setShowReAuthModal(false);

      // Small delay to ensure state updates have propagated
      setTimeout(async () => {
        try {
          await retryPendingRequest();
        } catch (error) {
          console.error(
            "❌ Failed to retry request after re-authentication:",
            error
          );
        }
      }, 100);
    } else {
      // Normal login (not re-auth)
      setShowReAuthModal(false);
    }
  };

  // Logout function
  const logout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("userData");
    setIsAuthenticated(false);
    setUserData(null);

    // Cancel any pending requests when logging out
    cancelPendingRequest();
  };

  // Function to trigger re-authentication modal
  const triggerReAuth = () => {
    setShowReAuthModal(true);
  };

  // Close modal without re-authenticating (user cancels)
  const handleCancelReAuth = () => {
    // Cancel the pending request since user chose not to re-authenticate
    cancelPendingRequest();

    // User chose to cancel - now we actually log them out
    logout();
    setShowReAuthModal(false);

    // Redirect to login page
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        userData,
        login,
        logout,
        triggerReAuth,
      }}
    >
      {children}

      {/* Re-authentication Modal */}
      {showReAuthModal && (
        <>
          {/* Backdrop overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
            onClick={handleCancelReAuth}
          />

          {/* Login modal */}
          <div className="fixed inset-0 flex items-center justify-center z-[9999] pointer-events-none">
            <div className="pointer-events-auto">
              <Login
                applicationName={applicationName}
                isReAuth={true}
                closeLoginForm={handleCancelReAuth}
              />
            </div>
          </div>
        </>
      )}
    </AuthContext.Provider>
  );
};
